import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { ListResponse } from '@shared/api';
import { http } from '@/services/http';

export function createResourceStore<T extends Record<string, unknown>>(id: string, endpoint: string) {
  return defineStore(id, () => {
    const rows = ref<T[]>([]);
    const loading = ref(false);
    const refreshing = ref(false);
    const loaded = ref(false);
    const error = ref('');
    const search = ref('');
    let requestId = 0;
    let inflight: Promise<void> | null = null;

    const filteredRows = computed(() => {
      const words = search.value.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      if (!words.length) return rows.value;
      return rows.value.filter((row) => {
        const haystack = Object.values(row)
          .filter((value) => ['string', 'number'].includes(typeof value))
          .join(' ')
          .toLocaleLowerCase();
        return words.every((word) => haystack.includes(word));
      });
    });

    function refresh(force: boolean): Promise<void> {
      if (inflight && !force) return inflight;
      const current = ++requestId;
      loading.value = !loaded.value;
      refreshing.value = loaded.value;
      if (!loaded.value) error.value = '';
      let pending!: Promise<void>;
      pending = (async () => {
        try {
          const result = await http.get<ListResponse<T>>(endpoint);
          if (current === requestId) {
            rows.value = result.rows || [];
            loaded.value = true;
            error.value = '';
          }
        } catch (cause) {
          if (current === requestId && !loaded.value) {
            error.value = cause instanceof Error ? cause.message : '加载失败';
          }
        } finally {
          if (current === requestId) {
            loading.value = false;
            refreshing.value = false;
          }
          if (inflight === pending) inflight = null;
        }
      })();
      inflight = pending;
      return pending;
    }

    function load(): Promise<void> {
      return refresh(false);
    }

    function ensureLoaded(): Promise<void> {
      return loaded.value ? Promise.resolve() : load();
    }

    async function reloadAfterMutation(): Promise<void> {
      if (inflight) await inflight;
      await refresh(true);
    }

    async function create(payload: Partial<T>): Promise<void> {
      await http.post(endpoint, payload);
      await reloadAfterMutation();
    }

    async function update(id: string | number, payload: Partial<T>): Promise<void> {
      await http.put(`${endpoint}/${encodeURIComponent(String(id))}`, payload);
      await reloadAfterMutation();
    }

    async function remove(id: string | number): Promise<void> {
      await http.delete(`${endpoint}/${encodeURIComponent(String(id))}`);
      await reloadAfterMutation();
    }

    return {
      rows,
      filteredRows,
      loading,
      refreshing,
      loaded,
      error,
      search,
      load,
      ensureLoaded,
      create,
      update,
      remove,
    };
  });
}
